<template>
	<section
		aria-label="Syrenis Help Assistant"
		data-testid="help-hero"
		:data-expanded="String(expanded)"
		class="help-hero"
	>
		<template v-if="expanded">
			<div
				data-testid="help-hero-toolbar"
				class="help-hero__toolbar"
			>
				<p
					data-testid="help-hero-subtitle"
					class="help-hero__title"
				>
					{{ activeTitle || 'New conversation' }}
				</p>
				<div class="help-hero__actions">
					<help-recent-menu
						v-if="hasRecent"
						v-model="recentOpen"
						:chats="chats"
						:active-chat-id="activeChatId"
						:max-items="RECENT_LIMIT"
						:disabled="streaming"
						@select="handleSelectRecent($event.chatId)"
					/>
					<button
						type="button"
						data-testid="help-hero-new-chat"
						:disabled="streaming"
						class="help-hero__action"
						@click="handleNewChat"
					>
						<LucideIcon
							name="Plus"
							:size="14"
						/>
						New chat
					</button>
					<button
						type="button"
						data-testid="help-hero-close"
						aria-label="Close conversation"
						:disabled="streaming"
						class="help-hero__action help-hero__action--icon"
						@click="handleClose"
					>
						<LucideIcon
							name="X"
							:size="16"
						/>
					</button>
				</div>
			</div>

			<!--
				Grows to its natural height instead of snapping to it, mirroring the platform hub's
				AssistantCard. The floor is what stops the flicker on send: the idle composer and
				chips leave together, so starting the row at zero would drop the section to the
				toolbar alone for a frame and twitch the page below.
			-->
			<div
				data-testid="help-hero-reveal"
				:class="['help-hero__reveal', { 'help-hero__reveal--open': entered }]"
			>
				<div class="help-hero__reveal-inner">
					<div
						aria-label="Help Assistant conversation"
						data-testid="help-hero-conversation"
						class="help-hero__conversation"
					>
						<help-assistant-panel
							:key="conversationKey"
							ref="assistant"
							chrome="embedded"
							context="admin-portal-splash"
							:suggested-questions="questions"
							:initial-chat-id="resumeChatId"
							:initial-message="pendingMessage"
							@chats-change="handleChatsChange"
							@active-chat-change="handleActiveChatChange"
							@streaming-change="handleStreamingChange"
						/>
					</div>
				</div>
			</div>
		</template>

		<div
			v-else
			:class="['help-hero__idle', { 'help-hero__idle--returning': hasOpened }]"
		>
			<help-hero-composer
				ref="composer"
				:questions="questions"
				@send="handleSubmit"
			>
				<template
					v-if="hasRecent"
					#trailing
				>
					<help-recent-menu
						v-model="recentOpen"
						:chats="chats"
						:active-chat-id="activeChatId"
						:max-items="RECENT_LIMIT"
						@select="handleSelectRecent($event.chatId)"
					/>
				</template>
			</help-hero-composer>
		</div>
	</section>
</template>

<script>
import { LucideIcon } from '@syrenis/cassie-material-design'
import HelpAssistantPanel from './help-assistant-panel.vue'
import HelpHeroComposer from './help-hero-composer.vue'
import HelpRecentMenu from './help-recent-menu.vue'
import { createHelpApiClient } from './utils/help-api-client.js'
import { getAuthToken } from '../../../state/modules/auth.js'
import { getEnvOptional } from '../../../../../shared/utils/runtime-settings.js'

/** How many recent conversations the menu lists. */
const RECENT_LIMIT = 8

/**
 * The Syrenis Help Assistant hero on the admin portal splash page (AB#27211), the Vue twin of the
 * platform hub's `AssistantCard`. Keep the two in step.
 *
 * Idle, it is an open section with a composer and suggestion chips. Asking a question replaces that
 * with the conversation: one bordered card carrying the transcript above its own field, which is how
 * the sheet reads. The caret follows the user across, so a question asked from the page-level field
 * continues in the conversation's field without a click, and without scrolling the page to reach it.
 *
 * A question typed from idle always starts a NEW chat; resuming is explicit via the Recent menu.
 * Closing collapses back to idle without losing the chat (it is persisted server-side and stays in
 * Recent). While an answer streams, Recent, New chat and Close are disabled so a turn cannot be torn
 * down by accident; the conversation's own Stop control is the way to abandon it.
 */
export default {
	name: 'help-hero',
	components: {
		LucideIcon,
		HelpAssistantPanel,
		HelpHeroComposer,
		HelpRecentMenu
	},
	props: {
		/** Product-aware suggestion chips for the idle composer. */
		questions: {
			type: Array,
			default: () => []
		}
	},
	data () {
		return {
			expanded: false,
			// '' means "start a new chat"; a chat id means "resume that one".
			resumeChatId: '',
			activeChatId: null,
			pendingMessage: '',
			conversationKey: 0,
			streaming: false,
			recentOpen: false,
			chats: [],
			// Entering a conversation animates; so does coming back. The idle composer on first
			// paint must not, or the page load gains an animation nobody asked for.
			hasOpened: false,
			// Drives the height grow. The surface mounts collapsed and opens on the NEXT frame, so
			// the browser has a start value to interpolate from; both in one commit would jump.
			entered: false,
			enterFrame: null,
			destroyed: false,
			RECENT_LIMIT
		}
	},
	computed: {
		hasRecent () {
			return this.chats.length > 0
		},
		activeTitle () {
			return this.chats.find(chat => chat.chatId === this.activeChatId)?.title ?? ''
		}
	},
	created () {
		// The idle state has no panel mounted, so it fetches the chat list itself: without this,
		// Recent would only appear after the user had opened a conversation once. A failure leaves
		// the list empty and simply hides the control, which is what the hub hero does.
		const baseUrl = getEnvOptional('VUE_APP_BASE_URL_PLATFORMAGENT')
		if (!baseUrl) return
		createHelpApiClient({ baseUrl, getToken: getAuthToken })
			.listChats()
			.then(list => {
				// The client exposes no abort, so the result is simply dropped if this component has
				// gone (navigation off the splash page) or the user has already opened a conversation,
				// whose own panel then owns the list.
				if (this.destroyed || this.expanded) return
				this.chats = list
			})
			.catch(() => {})
	},
	beforeDestroy () {
		this.destroyed = true
		this.cancelEnterFrame()
	},
	methods: {
		cancelEnterFrame () {
			if (this.enterFrame !== null) {
				cancelAnimationFrame(this.enterFrame)
				this.enterFrame = null
			}
		},
		openConversation (chatId, message = '') {
			const wasExpanded = this.expanded
			this.resumeChatId = chatId ?? ''
			this.activeChatId = chatId ?? null
			this.pendingMessage = message
			this.streaming = false
			this.recentOpen = false
			this.conversationKey += 1
			this.hasOpened = true
			this.expanded = true
			// Only animate the open. Switching chats while already open would otherwise collapse the
			// surface towards its floor and grow it again, which the React original never does.
			if (!wasExpanded) {
				this.entered = false
				this.cancelEnterFrame()
				this.enterFrame = requestAnimationFrame(() => {
					this.entered = true
					this.enterFrame = null
				})
			}
			// Focus lands on the conversation's own field, so whoever just asked a question can type
			// the next one straight away rather than being stranded on a container.
			this.$nextTick(() => this.$refs.assistant?.focusComposer())
		},
		handleSubmit ({ text }) {
			this.openConversation(null, text)
		},
		handleSelectRecent (chatId) {
			this.openConversation(chatId)
		},
		handleNewChat () {
			this.openConversation(null)
		},
		handleClose () {
			this.cancelEnterFrame()
			this.entered = false
			this.recentOpen = false
			this.pendingMessage = ''
			this.resumeChatId = ''
			this.activeChatId = null
			this.streaming = false
			this.expanded = false
			this.$nextTick(() => this.$refs.composer?.focus())
		},
		handleChatsChange (list) {
			this.chats = list
		},
		handleActiveChatChange (chatId) {
			this.activeChatId = chatId
		},
		handleStreamingChange (value) {
			this.streaming = value
		}
	}
}
</script>

<style lang="scss" scoped>
.help-hero {
	display: flex;
	flex-direction: column;
	gap: 12px;
	text-align: left;

	&__toolbar {
		display: flex;
		flex-wrap: wrap;
		gap: 12px;
		align-items: center;
		justify-content: space-between;
		animation: help-hero-fade 300ms ease-out;
	}

	&__title {
		min-width: 0;
		margin: 0;
		overflow: hidden;
		font-size: 14px;
		font-weight: 500;
		color: var(--foreground, #4A5568);
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	&__actions {
		display: flex;
		gap: 4px;
		align-items: center;
	}

	&__action {
		display: flex;
		gap: 6px;
		align-items: center;
		min-height: 32px;
		padding: 4px 8px;
		font-size: 0.8125rem;
		color: var(--muted-foreground, #666E7A);
		cursor: pointer;
		background: none;
		border: none;
		border-radius: var(--radius-sm, 4px);
		transition: background-color 0.15s ease, color 0.15s ease;

		&:hover:not(:disabled) {
			color: var(--foreground, #4A5568);
			background-color: var(--muted, #F5F5F5);
		}

		&:disabled {
			cursor: not-allowed;
			opacity: 0.5;
		}

		&:focus:not(:focus-visible) {
			outline: none;
		}

		&:focus-visible {
			outline: 2px solid var(--global-primary, #406A94);
			outline-offset: 2px;
		}

		&--icon {
			justify-content: center;
			min-width: 32px;
			padding: 4px;
		}
	}

	// A grid row interpolated towards 1fr is the only way to animate to a content-driven height in
	// CSS, and it degrades cleanly: a browser that cannot interpolate it simply arrives open. The
	// 2.75rem floor is the height the idle composer and chips give up, which keeps the swap
	// height-neutral instead of twitching the page below for a frame.
	&__reveal {
		display: grid;
		grid-template-rows: minmax(2.75rem, 0fr);
		transition: grid-template-rows 500ms ease-out;

		&--open {
			grid-template-rows: minmax(2.75rem, 1fr);
		}

		@media (prefers-reduced-motion: reduce) {
			transition: none;
		}
	}

	&__reveal-inner {
		overflow: hidden;
	}

	&__conversation {
		display: flex;
		flex-direction: column;
		max-height: 480px;
		min-height: 240px;
		background-color: var(--card, #FFFFFF);
		border: 1px solid var(--border, #E0E2E5);
		border-radius: var(--radius, 8px);
		animation: help-hero-fade 500ms ease-out;
	}

	&__idle--returning {
		animation: help-hero-fade 200ms ease-out;
	}

	&__recent-menu {
		width: 320px;
		padding: 8px;
		background-color: var(--popover, #FFFFFF);
		border: 1px solid var(--border, #E0E2E5);
		border-radius: var(--radius, 8px);
	}

	&__recent-label {
		margin: 0 0 4px;
		font-size: 0.75rem;
		font-weight: 600;
		color: var(--muted-foreground, #666E7A);
		text-transform: uppercase;
		letter-spacing: 0.06em;
	}

	&__recent-item {
		display: flex;
		gap: 12px;
		align-items: center;
		width: 100%;
		min-height: 40px;
		padding: 0 12px;
		font-size: 0.8125rem;
		color: var(--foreground, #4A5568);
		text-align: left;
		cursor: pointer;
		background: none;
		border: none;
		border-radius: var(--radius-sm, 4px);

		&:hover {
			background-color: var(--muted, #F5F5F5);
		}

		&[aria-current='true'] {
			font-weight: 600;
			background-color: var(--muted, #F5F5F5);
		}

		&:focus:not(:focus-visible) {
			outline: none;
		}

		&:focus-visible {
			outline: 2px solid var(--global-primary, #406A94);
			outline-offset: 2px;
		}
	}

	&__recent-title {
		flex: 1 1 auto;
		min-width: 0;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	&__recent-time {
		flex: none;
		font-size: 0.75rem;
		color: var(--muted-foreground, #666E7A);
	}
}

@keyframes help-hero-fade {
	from {
		opacity: 0;
	}

	to {
		opacity: 1;
	}
}

@media (prefers-reduced-motion: reduce) {
	.help-hero__toolbar,
	.help-hero__conversation,
	.help-hero__idle--returning {
		animation: none;
	}
}
</style>
